// ** React Imports
import { useState, useEffect, forwardRef, useCallback, Fragment, useMemo, useRef } from "react";

// ** MUI Imports
import {
  Box,
  Checkbox,
  Drawer,
  Button,
  MenuItem,
  IconButton,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Grid,
  CircularProgress
} from "@mui/material";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { TimePicker } from '@mui/x-date-pickers-pro';
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
// ** Custom Component Import
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";

// ** Third Party Imports
// import DatePicker from "react-datepicker";
import { useForm, Controller } from "react-hook-form";

// ** Icon Imports
import Icon from "components/icon";

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

// ** Styled Components
import DatePickerWrapper from "components/styles/libs/react-datepicker";
import { GetCustomer } from "screens/Customers/logic/GetCustomer";
import { GetJobsite } from "screens/Customers/logic/GetJobsites";
import { useNavigate } from "react-router-dom";
import { deleteAttachmentAPI, getAttachments, getServiceRequests, getServices, getWorkRequirements, uploadAttachment } from "screens/DispatchBoard/logic/DispatchBoardLogic";
import { GetEmployees } from "screens/Dashboard/logic/PunchClockLogic";
import dayjs from "dayjs";
import { SaveServiceRequest } from "screens/Requests/logic/SaveServiceRequest";
import { saveWorkRequirementSchedule } from "screens/WorkOrders/logic/WorkRequirementScheduleLogic";

const capitalize = (string) =>
  string && string[0].toUpperCase() + string.slice(1);


const AddEventSidebar = (props) => {

  // ** Props
  const {
    store,
    dispatch,
    addEvent,
    updateEvent,
    drawerWidth,
    calendarApi,
    deleteEvent,
    handleSelectEvent,
    addEventSidebarOpen,
    handleAddEventSidebarToggle,
    toast,
    onJobCreated
  } = props;

  const defaultState = {
    requestID:   0,
    workReqID:   0,
    jobID:       0,
    url: "",
    title: "",
    guests: [],
    allDay: true,
    description: "",
    // endDate: new Date(),
    calendar: "Job",
    // startDate: new Date(),
    customer: null,
    jobsite: null,
    service: null,
    assignees: [],
    instructions: [],
    startDateTime: null,
    endDateTime: null,
    requestTitle: ""
  };
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [values, setValues] = useState(defaultState);
  const [fieldErrors, setFieldErrors] = useState({});
  const [customers, setCustomers] = useState([]);
  const [jobsites, setJobsites]     = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [attachment, setAttachment] = useState(null)
  const [startDateTime, setStartDateTime] = useState(defaultState.startDateTime);
  const [endDateTime, setEndDateTime] = useState(defaultState.startDateTime);
  
  // Separate date and time states for UI
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [originalAttachment, setOriginalAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const MySwal = withReactContent(Swal);
  const originalSnapshot = useRef(null);

  const isEdit     = Boolean(store.selectedEvent);
  // if editing, keep whatever calendar was on the original event;
  // otherwise default to WorkOrder
  const entityType = store.selectedEvent?.extendedProps.calendar || 'WorkOrder';

  const isUpdateMode = Boolean(store.selectedEvent);

  // Sync separate date/time states with combined startDateTime
  useEffect(() => {
    if (startDateTime) {
      setStartDate(dayjs(startDateTime));
      setStartTime(dayjs(startDateTime));
    } else {
      setStartDate(null);
      setStartTime(null);
    }
  }, [startDateTime]);
  
  // Sync separate date/time states with combined endDateTime
  useEffect(() => {
    if (endDateTime) {
      setEndDate(dayjs(endDateTime));
      setEndTime(dayjs(endDateTime));
    } else {
      setEndDate(null);
      setEndTime(null);
    }
  }, [endDateTime]);
  
  useEffect(() => {
    if (isUpdateMode && startDateTime) {
      // For update mode (Assessment), set end time to 1 hour after start time
      const endTime = dayjs(startDateTime).add(1, 'hour');
      setEndDateTime(endTime);
    }
  }, [startDateTime, isUpdateMode]);
  
  // Add this handler to prevent manual end time changes in update mode
  const handleEndDateTimeChange = (date) => {
    if (!isUpdateMode) {
      setEndDateTime(date);
    }
    // For update mode, don't allow manual changes - it's auto-set to 1 hour after start
  };
  
  // Handlers for separate date/time pickers
  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date && startTime) {
      const combined = dayjs(date)
        .hour(dayjs(startTime).hour())
        .minute(dayjs(startTime).minute());
      setStartDateTime(combined);
    } else if (date) {
      setStartDateTime(date);
    }
  };
  
  const handleStartTimeChange = (time) => {
    setStartTime(time);
    if (startDate && time) {
      const combined = dayjs(startDate)
        .hour(dayjs(time).hour())
        .minute(dayjs(time).minute());
      setStartDateTime(combined);
    }
  };
  
  const handleEndDateChange = (date) => {
    if (!isUpdateMode) {
      setEndDate(date);
      if (date && endTime) {
        const combined = dayjs(date)
          .hour(dayjs(endTime).hour())
          .minute(dayjs(endTime).minute());
        setEndDateTime(combined);
      } else if (date) {
        setEndDateTime(date);
      }
    }
  };
  
  const handleEndTimeChange = (time) => {
    if (!isUpdateMode) {
      setEndTime(time);
      if (endDate && time) {
        const combined = dayjs(endDate)
          .hour(dayjs(time).hour())
          .minute(dayjs(time).minute());
        setEndDateTime(combined);
      }
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    // on mount: load all customers
    (async () => {
      const all = await GetCustomer(0, navigate);
      setCustomers(all);
    })();
  
    // load services
    (async () => {
      try {
        const s = await getServices(navigate);
        setServices(s);
      } catch (err) {
        console.error("Error fetching services", err);
      }
    })();

    (async () => {
      try {
        const resp = await GetEmployees(0);         // wrapper must return the full data obj
        setEmployees(resp.result || []);            // grab the array
      } catch (err) {
        console.error("Error loading employees", err);
      }
    })();

  }, []);

  const handleCustomerChange = async (_, customer) => {
    setValues(v => ({ ...v, customer, jobsite: null }));
    if (customer) {
      // pass jobsiteID = 0 to get all jobsites for that customer
      const sites = await GetJobsite(customer.customerID, 0, navigate);
      setJobsites(sites);
    } else {
      setJobsites([]);
    }
  };
  
  const handleJobsiteChange = (_, jobsite) => {
    setValues(v => ({ ...v, jobsite }));
  };

  

  const handleServiceChange = (_, service) => {
    setValues(v => ({ ...v, service }));
  };

  const handleAssigneesChange = (_, newAssignees) => {
    setValues(v => ({ ...v, assignees: newAssignees }));
  };

  const handleAddInstruction = () => {
    if (values.instructions.length < 10) {
      setValues(v => ({
        ...v,
        instructions: [
          ...v.instructions,
          { specialInstructionID: 0, instructionText: "" }
        ]
      }));
    }
  }
  
  const handleInstructionChange = (idx, e) => {
    const copy = [ ...values.instructions ];
    copy[idx].instructionText = e.target.value;
    setValues(v => ({ ...v, instructions: copy }));
  };
  
  const handleRemoveInstruction = idx => {
    setValues(v => ({
      ...v,
      instructions: v.instructions.filter((_, i) => i !== idx)
    }))
  }

  const handleFileChange = e => {
    const file = e.target.files?.[0]
    if (!file) return
  
    if (file.size > 2 * 1024 * 1024) {
      // you could swap alert for your preferred toast/snackbar
      toast.error("Attachment can be only 2 MB or smaller!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      return
    }
  
    setAttachment(file)
  }

    // // field handlers
    // const handleCustomerChange = (_, customer) => {
    //   setValues(v => ({ ...v, customer, jobsite: null }));
    // };
    // const handleJobsiteChange = (_, jobsite) => {
    //   setValues(v => ({ ...v, jobsite }));
    // };
    // const handleServiceChange = (_, service) => {
    //   setValues(v => ({ ...v, service }));
    // };
    const handleRequestTitleChange = e => {
      setValues(v => ({ ...v, requestTitle: e.target.value }));
    };
    const handleDescriptionChange = e => {
      setValues(v => ({ ...v, description: e.target.value }));
    };
    // Removed duplicate handlers - using the ones defined above for separate date/time pickers

  const validateFields = () => {
    const errs = {};
    if (!values.customer) errs.customer = 'Customer is required';
    if (!values.jobsite)  errs.jobsite  = 'Jobsite is required';
    
    const entityType = store.selectedEvent?.extendedProps.calendar || values.calendar;
    if (!values.service && entityType !== 'Assessment')
      errs.service   = 'Service is required';

    if (!values.requestTitle?.trim()) errs.requestTitle = 'Title is required';
    // if (!values.description.trim())   errs.description   = 'Description is required';
    if (!startDateTime) errs.startDateTime = `${isUpdateMode ? 'Date & Time' : 'Start Date & Time'} is required`;
  
    // Only validate end date/time for create mode (not update mode)
    if (!isUpdateMode) {
      if (!endDateTime) errs.endDateTime = 'End Date & Time is required';
      
      if (startDateTime && endDateTime && dayjs(startDateTime).isAfter(dayjs(endDateTime))) {
        errs.startDateTime = 'Start Date Time must be before End Date Time';
      }
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // inside your component
  const hasUnsavedChanges = useMemo(() => {
    // CREATE mode
    if (!store.selectedEvent) {
      return (
        JSON.stringify(values) !== JSON.stringify(defaultState) ||
        startDateTime !== null ||
        endDateTime   !== null ||
        attachment    !== null
      );
    }
  
    // EDIT mode, but snapshot not ready → no changes
    if (!originalSnapshot.current) {
      return false;
    }
  
    // EDIT mode, snapshot present → do your deep-compare
    const { values: origValues, startDateTime: origStart, endDateTime: origEnd, attachmentName: origAttach } =
      originalSnapshot.current;
  
    const current = {
      ...values,
      startDateTime: startDateTime?.toISOString(),
      endDateTime:   endDateTime?.toISOString(),
      attachmentName:
        attachment instanceof File ? attachment.name : origAttach
    };
  
    const original = {
      ...origValues,
      startDateTime: origStart?.toISOString(),
      endDateTime:   origEnd?.toISOString(),
      attachmentName: origAttach
    };
  
    return JSON.stringify(current) !== JSON.stringify(original);
  }, [values, startDateTime, endDateTime, attachment, store.selectedEvent]);
  
const handleDiscardChanges = () => {
  setIsDialogOpen(false);
  handleSidebarClose();
};

const handleDialogClose = () => {
  setIsDialogOpen(false);
};
const handleDrawerCloseRequest = (event, reason) => {
  if (
    (reason === "backdropClick" || reason === "escapeKeyDown")
    && hasUnsavedChanges
  ) {
    event.preventDefault(); 
     setIsDialogOpen(true);// stop immediate close
    // MySwal.fire({
    //   title: "Are you sure?",
    //   text: "You have unsaved changes.",
    //   icon: "warning",
    //   showCancelButton: true,
    //   confirmButtonText: "Discard changes",
    //   cancelButtonText: "Keep editing",
    //   confirmButtonColor: "#23A26D",
    //   cancelButtonColor: "#d33",
    // }).then(result => {
    //   if (result.isConfirmed) {
    //     handleSidebarClose();  // your existing cleanup + close
    //   }
    //   // else: do nothing, leave drawer open
    // });
  } else {
    // no unsaved changes, or user clicked the “X” button,
    // so just close as normal:
    handleSidebarClose();
  }
};



  const onSubmit = async () => {
    if (!validateFields()) return;
    
    // const isEdit = Boolean(store.selectedEvent)

    const originalCal      = store.selectedEvent?.extendedProps.calendar;

    // const entityType       = isEdit ? originalCal : 'WorkOrder';

    let origSR = {}, origWR = {};
    if (isEdit) {
      setSubmitting(true);
      const [allSRs, allWRs] = await Promise.all([
        getServiceRequests(navigate),
        getWorkRequirements(navigate)
      ]); 
      origSR = allSRs.find(s => s.requestID === values.requestID) || {};
      origWR = allWRs.find(w => w.workReqID   === values.workReqID)  || {};
    }

    const primaryContactID =
    values.jobsite?.contact?.find(c => c.isPrimary)?.contactID || 0;

    const serviceRequestPayload = {
      requestID:     isEdit
        ? values.requestID
        : 0,
      requestTitle:  isEdit
        ? (values.requestTitle || origSR.requestTitle)
        : values.requestTitle,
      reqDescription: isEdit
        ? (values.description  || origSR.reqDescription)
        : values.description,
      // only override these if user changed them
      customerID:    isEdit
        ? (values.customer?.customerID || origSR.customerID)
        : values.customer.customerID,
      jobSiteID:     isEdit
        ? (values.jobsite?.jobsiteID   || origSR.jobSiteID)
        : values.jobsite.jobsiteID,
      jobsiteContactID: isEdit
        ? (values.jobsite?.contact?.find(c=>c.isPrimary)?.contactID
           || origSR.jobsiteContactID)
        : (values.jobsite?.contact?.find(c=>c.isPrimary)?.contactID || 0),
      requestSource:   origSR.requestSource || "Internal",
      status:          origSR.status  || "Submitted",
      availabilityTime: origSR.availabilityTime || [],
      availabilityDays: origSR.availabilityDays || [],
      tags:             origSR.tags || [],
      assignedToUsers:  origSR.assignedToUsers || null,
      entityType
    };
    setSubmitting(true);
  
    try {
      // POST #1: save ServiceRequest
      const srResponse = await SaveServiceRequest(
        serviceRequestPayload,
        navigate,
        entityType
      );

      // assume the API returns { result: { requestID: <newID>, … } }
      const newRequestID = isEdit
      ? values.requestID
      : srResponse?.result?.requestID;

      if (!newRequestID) {
        return toast.error(
          `Error ${isEdit ? 'updating' : 'creating'} ${entityType}.`,
          { position: 'top-right' }
        );
      }

      const subServiceID =
        entityType === 'Assessment'
          ? 0
          : values.service?.serviceOfferingID;

          const matchedJob = isEdit
          ? origWR.jobs?.find(j => j.jobID === values.jobID)
          : null;

          const occurrenceNo = entityType === "Assessment"
            ? 0
            : (matchedJob?.occurrenceNo ?? 1);

      // 2) Build WorkRequirement payload
      const workReqPayload = {
        workReqID:           isEdit ? values.workReqID                    : 0,
        entityID:            newRequestID,
        subServiceID,
        bundleID:            isEdit ? origWR.bundleID                    : 0,
        // propertyAreaIDs:     isEdit ? origWR.propertyAreaIDs             : [],
        description:         isEdit ? (origWR.description || null)       : null,
        jobType:             isEdit ? origWR.jobType                     : "One Time",
        billingType:         isEdit ? origWR.billingType                 : "Fixed",
        frequency:           isEdit ? origWR.frequency                   : null,
        numberOfOccurrences: isEdit ? origWR.numberOfOccurrences         : null,
        firstOccurrenceDate: isEdit
          ? origWR.firstOccurrenceDate
          : dayjs(startDateTime).format("YYYY-MM-DD"),
        excludeWeekends:     isEdit ? origWR.excludeWeekends             : false,
        excludeHolidays:     isEdit ? origWR.excludeHolidays             : false,
        // daysOfWeek:          isEdit ? origWR.daysOfWeek                  : null,
        // weekConfiguration:   isEdit ? origWR.weekConfiguration           : null,
        // monthConfiguration:  isEdit ? origWR.monthConfiguration          : null,
        // intervalDays:        isEdit ? origWR.intervalDays                : null,
        specialInstructions: values.instructions.map(i => ({
          specialInstructionID: i.specialInstructionID,
          instructionText:      i.instructionText,
          isAlert:              true
        })),
        jobs: [
          {
            jobID:          isEdit ? values.jobID                       : 0,
            flatRate:       isEdit
              ? (origWR.jobs?.[0]?.flatRate     || 0.00)
              : 0.00,
            billingType:    isEdit
              ? (origWR.jobs?.[0]?.billingType  || "Fixed")
              : "Fixed",
            occurrenceNo,
            scheduleStartDate: dayjs(startDateTime).format("YYYY-MM-DD"),
            scheduleStartTime: dayjs(startDateTime).format("HH:mm:ss"),
            scheduleEndDate: isUpdateMode 
              ? dayjs(startDateTime).add(1, 'hour').format("YYYY-MM-DD") // Auto 1 hour for update mode
              : dayjs(endDateTime).format("YYYY-MM-DD"),
            scheduleEndTime: isUpdateMode 
              ? dayjs(startDateTime).add(1, 'hour').format("HH:mm:ss") // Auto 1 hour for update mode
              : dayjs(endDateTime).format("HH:mm:ss"),
            scheduleMethod: isEdit
              ? origWR.jobs?.[0]?.scheduleMethod
              : null,
            reminder:       isEdit
              ? origWR.jobs?.[0]?.reminder
              : null,
            reminderTime:   isEdit
              ? origWR.jobs?.[0]?.reminderTime
              : null,
            assignees:      values.assignees.map(u => u.employeeID)
          }
        ]
      };

      // POST #2: save WorkRequirement
      //console.log(workReqPayload)
      const wrResponse = await saveWorkRequirementSchedule(
        workReqPayload,
        navigate
      );
      //  {console.log("wrResponse", wrResponse)}
       

      // both calls succeeded
    // 3) if we have a file, upload it now
      if (isEdit) {
        // 1) if there was an original and now the user removed it:
        if (originalAttachment && !attachment) {
          await deleteAttachmentAPI(values.requestID, originalAttachment.mediaID);
        }
        // 2) if there was an original and the user picked a new file (different name):
        else if (
          originalAttachment &&
          attachment instanceof File &&
          attachment.name !== originalAttachment.fileName
        ) {
          // delete the old one…
          await deleteAttachmentAPI(values.requestID, originalAttachment.mediaID);
          // …then upload the new one
          await uploadAttachment(values.requestID, attachment);
        }
        // 3) if no original (first‐time create) & there is a file:
        else if (!originalAttachment && attachment instanceof File) {
          await uploadAttachment(values.requestID, attachment);
        }
        // else: same file name ⇒ do nothing
      } else {
        // CREATE mode: just upload if user picked a file
        if (attachment) {
          await uploadAttachment(newRequestID, attachment);
        }
      }
      toast.success(`Quick Job ${isEdit ? 'updated' : 'created'} successfully!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });

      // after saving SR & WR...     
      // dispatch(fetchEvents(['Job','Assessment']));
      // you might close the sidebar or show a success toast here
      handleSidebarClose();

//       const handleDiscardChanges = () => {
//   setIsDialogOpen(false);
//   handleSidebarClose();
// };

// const handleDialogClose = () => {
//   setIsDialogOpen(false);
// };

const handleDrawerCloseRequest = (event, reason) => {
  if (
    (reason === "backdropClick" || reason === "escapeKeyDown")
    && hasUnsavedChanges
  ) {
    event.preventDefault(); // stop immediate close
    setIsDialogOpen(true);
  } else {
    // no unsaved changes, or user clicked the "X" button,
    // so just close as normal:
    handleSidebarClose();
  }
};



      const ev = {
        id:        newRequestID.toString(),    // FullCalendar wants string IDs
        title:     values.requestTitle,
        start:     dayjs(startDateTime).toDate(),
        end:       dayjs(endDateTime).toDate(),
        extendedProps: {
          calendar:    entityType,                  // or "Assessment"
          requestID:   newRequestID,
          workReqID:   wrResponse.result.workReqID,
          jobID:       wrResponse.result.jobs?.[0]?.jobID || values.jobID,
          jobType : origWR.jobType,
          
          customer:    values.customer,
          jobsite:     values.jobsite,
          service:     values.service,
          assignees:   values.assignees,
          instructions:values.instructions,
          description: values.description,
          attachment,      
                             // if you want to round-trip
          // …any other bits you need
        }
      }
    
      // if (isEdit) {
      //   const old = calendarApi.getEventById(ev.id);
      //   if (old) old.remove();
      // }

      // calendarApi.addEvent(ev);
      // dispatch(addEvent(ev))
      if (calendarApi) {
        if (isEdit) {
          const old = calendarApi.getEventById(ev.id);
          if (old) old.remove();
        }
        calendarApi.addEvent(ev);
      }
      dispatch(addEvent(ev));

      onJobCreated?.();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error('An error occurred. Please try again.', { position: 'top-right' });
      // show an error toast/snackbar as needed
    } finally {
    
      setSubmitting(false);
    
    }

  };
   

  const {
    control,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { title: "" } });

  const handleSidebarClose = async () => {
    //setValues(defaultState);
    handleAddEventSidebarToggle();
    resetToEmptyValues()
    clearErrors();
    setFieldErrors({}); 
    dispatch(handleSelectEvent(null));    
  };

  // const onSubmit = (data) => {
  //   const modifiedEvent = {
  //     url: values.url,
  //     display: "block",
  //     title: data.title,
  //     end: values.endDate,
  //     allDay: values.allDay,
  //     start: values.startDate,
  //     extendedProps: {
  //       calendar: capitalize(values.calendar),
  //       guests:
  //         values.guests && values.guests.length ? values.guests : undefined,
  //       description: values.description.length ? values.description : undefined,
  //     },
  //   };
  //   if (
  //     store.selectedEvent === null ||
  //     (store.selectedEvent !== null && !store.selectedEvent.title.length)
  //   ) {
  //     dispatch(addEvent(modifiedEvent));
  //   } else {
  //     dispatch(updateEvent({ id: store.selectedEvent.id, ...modifiedEvent }));
  //   }
  //   calendarApi.refetchEvents();
  //   handleSidebarClose();
  // };

  const handleDeleteEvent = () => {
    if (store.selectedEvent) {
      dispatch(deleteEvent(store.selectedEvent.id));
    }

    // calendarApi.getEventById(store.selectedEvent.id).remove()
    handleSidebarClose();
  };

  const handleStartDate = (date) => {
    if (date > values.endDate) {
      setValues({
        ...values,
        startDate: new Date(date),
        endDate: new Date(date),
      });
    }
  };

  useEffect(() => {
    if (!store.selectedEvent) return
  
    // 1) grab all the files for this request
    const id = store.selectedEvent.extendedProps.requestID
    getAttachments(id).then(files => {
      const next = files[0] || null;
      setOriginalAttachment(next);
      setAttachment(next);
    })
  }, [ store.selectedEvent ])

  useEffect(() => {
    if (isEdit) {
      resetToStoredValues()
    } else {
      // no selectedEvent ⇒ use the calendar click date props if provided,
      // otherwise fallback to totally blank
      setValues(defaultState)
      setStartDateTime(props.defaultCreateStart ?? null)
      setEndDateTime(  props.defaultCreateEnd   ?? null)
      setAttachment(null)
      setOriginalAttachment(null)
    }
  }, [ store.selectedEvent, addEventSidebarOpen, /*…*/ ])

  const custList = customers || []
  const svcList  = services  || []
  const empList  = employees || []

  const resetToStoredValues = useCallback(async () => {
    const ev = store.selectedEvent
    // console.log("Resetting to stored values:", ev);
    if (!ev) return
  
    // fetch the right jobsites for that customer
    const rawInsts = ev.extendedProps.specialInstructions || [];
    const custID = ev.extendedProps.customerID

    const {
      requestTitle,
      requestID,
      workReqID,
      jobID,
      jobType,
      occurrenceNo,
      assignees: assigneeIds = [],
      specialInstructions: insts = [],
      description = "",
      customerID,
      jobSiteID,
      subServiceID
    } = ev.extendedProps

    const jsites = await GetJobsite(customerID, 0, navigate)
    setJobsites(jsites || [])

    // now pick the exact objects out of our arrays
    const custObj    = custList.find(c => c.customerID === customerID) || null
    const jobsiteObj = (jsites || []).find(j => j.jobsiteID === jobSiteID) || null
    const svcObj     = svcList.find(s => s.serviceOfferingID === subServiceID) || null
    // const assigneeObjs = empList.filter(e => assigneeIds.includes(e.employeeID))

    let assigneeObjs = [];
    if (Array.isArray(assigneeIds)) {
      // If assignees is already an array of objects
      assigneeObjs = assigneeIds;
    } else if (Array.isArray(ev.extendedProps.assignees)) {
      // If assignees is an array of IDs, find the employee objects
      assigneeObjs = empList.filter(e => 
        ev.extendedProps.assignees.includes(e.employeeID)
      );
    }
    
    // setValues({
    //   ...defaultState,
    //   requestID,
    //   workReqID,
    //   jobID,
    //   customer:     custObj,
    //   jobsite:      jobsiteObj,
    //   service:      svcObj,
    //   assignees:    assigneeObjs,
    //   instructions: rawInsts.map(i => ({
    //     specialInstructionID: i.specialInstructionID,
    //     instructionText:      i.instructionText
    //   })),
    //   description:  ev.extendedProps.description  || "",
    //   requestTitle:  ev.extendedProps.requestTitle || "",
    // })
    // setValues({
    //   ...defaultState,
    //   requestID,
    //   workReqID,
    //   jobID,
    //   customer:     custObj,
    //   jobsite:      jobsiteObj,
    //   service:      svcObj,
    //   assignees:    assigneeObjs,
    //   instructions: insts.map(i => ({
    //     specialInstructionID: i.specialInstructionID,
    //     instructionText:      i.instructionText
    //   })),
    //   description,
    //   requestTitle
    // })
    const loadedValues = {
      ...defaultState,
      requestID,
      workReqID,
      jobID,
      customer:    custObj,
      jobsite:     jobsiteObj,
      service:     svcObj,
      assignees:   assigneeObjs,
      instructions: insts.map(i => ({
        specialInstructionID: i.specialInstructionID,
        instructionText:      i.instructionText
      })),
      description,
      requestTitle,
    };
    const loadedStart = dayjs(ev.start);
    const loadedEnd   = dayjs(ev.end);
    const loadedAttachName = originalAttachment?.fileName || null;

    // setStartDateTime(dayjs(ev.start));
    // setEndDateTime(  dayjs(ev.end  ));
    setValues(loadedValues);
    setStartDateTime(loadedStart);
    setEndDateTime(loadedEnd);
    setAttachment(originalAttachment);       // if you keep the file object
    setOriginalAttachment(originalAttachment);
   
    originalSnapshot.current = {
      values: loadedValues,
      startDateTime: loadedStart,
      endDateTime:   loadedEnd,
      attachmentName: loadedAttachName
    };

  }, [ 
    store.selectedEvent,
    navigate,
    custList,
    svcList,
    empList
   ])
  
  const resetToEmptyValues = useCallback(() => {
    setValues(defaultState)
    setStartDateTime(null)
    setEndDateTime(  null)
    setAttachment(null)
    setOriginalAttachment(null)
  }, [])
  
  // 3) single effect to drive both Create vs. Edit
  useEffect(() => {
    // console.log("Selected Event changed:", store.selectedEvent);
    if (store.selectedEvent) {
      resetToStoredValues()
    } else {
      resetToEmptyValues()
    }
  }, [
    store.selectedEvent,
    addEventSidebarOpen,
    employees,
    resetToStoredValues,
    resetToEmptyValues,
  ])

  const PickersComponent = forwardRef(({ ...props }, ref) => {
    return (
      <CustomTextField
        inputRef={ref}
        fullWidth
        {...props}
        label={props.label || ""}
        sx={{ width: "100%" }}
        error={props.error}
      />
    );
  });

const RenderSidebarFooter = () => {
  const isUpdate = Boolean(store.selectedEvent && store.selectedEvent.title.length);

  return (
    <>
      <Button
        type="submit"
        variant="contained"
        color="main"
        sx={{ mr: 4 }}
        disabled={submitting}
      >
        {submitting
          ? <CircularProgress size={20} color="inherit" />
          : (isUpdate ? "Save Changes" : "Create Job")
        }
      </Button>
      <Button
        variant="contained"
        color="info"
        onClick={handleSidebarClose}
        disabled={submitting}
      >
        Cancel
      </Button>
    </>
  );
};
  return (
    <>
    <Drawer
      anchor="right"
      open={addEventSidebarOpen}
      onClose={handleDrawerCloseRequest}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: ["100%", drawerWidth] } }}
    >
      <Box
        className="sidebar-header"
        sx={{
          p: 6,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5" display="flex" alignItems="center">
  {store.selectedEvent !== null && store.selectedEvent.title.length
    ? "Update Assessment"
    : "Create Job"}

{store.selectedEvent && (
  <Box
    component="span"
    sx={{
      ml: 3,
      px: 2,
      py: 0.5,
      fontSize: '0.75rem',
      borderRadius: 1,
      color: '#033278',
      backgroundColor:
        store.selectedEvent.extendedProps.occurrenceNo > 1
          ? '#ECF1FD'
          : '#ECF1FD'
    }}
  >
    { store.selectedEvent.extendedProps.occurrenceNo > 1 ? 'Recurring' : 'One Time'}
  </Box>
)}
</Typography>

{/* {console.log("store", store)}
{console.log("store.selectedEvent.extendedProps", store.selectedEvent?.extendedProps)}
{console.log("store.selectedEvent.extendedProps.numberOfOccurrences", store.selectedEvent?.extendedProps?.numberOfOccurrences)}
{console.log("store.selectedEvent.extendedProps.jobType", store.selectedEvent?.extendedProps?.jobType)}
{console.log("store.selectedEvent.extendedProps.frequency", store.selectedEvent?.extendedProps?.frequency)} */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* {store.selectedEvent !== null && store.selectedEvent.title.length ? (
            <IconButton
              size="small"
              onClick={handleDeleteEvent}
              sx={{
                color: "text.primary",
                mr: store.selectedEvent !== null ? 1 : 0,
              }}
            >
              <Icon icon="tabler:trash" fontSize="1.25rem" />
            </IconButton>
          ) : null} */}
          <IconButton
            size="small"
            onClick={handleSidebarClose}
            sx={{
              p: "0.375rem",
              borderRadius: 1,
              color: "text.primary",
              backgroundColor: "action.selected",
              "&:hover": {
                backgroundColor: (theme) =>
                  `rgba(${theme.palette.customColors.main}, 0.16)`,
              },
            }}
          >
            <Icon icon="tabler:x" fontSize="1.25rem" />
          </IconButton>
        </Box>
      </Box>
      <Box
        className="sidebar-body"
        sx={{ p: (theme) => theme.spacing(0, 6, 6) }}
      >
        <DatePickerWrapper>
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <CustomAutocomplete
                fullWidth
                sx={{ mb: 4 }}
                options={
                  custList
                    ? [...custList].sort((a, b) => {
                        const nameA =
                          a.customerType === "Individual"
                            ? `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase()
                            : (a.companyName ?? "").toLowerCase();
                        const nameB =
                          b.customerType === "Individual"
                            ? `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase()
                            : (b.companyName ?? "").toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                    : []
                }
                getOptionLabel={(opt) =>
                  opt.customerType === "Individual"
                    ? `${opt.firstName ?? ""} ${opt.lastName ?? ""}`.trim()
                    : opt.companyName ?? ""
                }
                value={values.customer}
                onChange={handleCustomerChange}
                disabled={Boolean(store.selectedEvent)}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Customer Name *"
                    placeholder="Select Customer"
                    error={!!fieldErrors.customer}
                    helperText={fieldErrors.customer}
                  />
                )}
              />
            <CustomAutocomplete
              fullWidth
              sx={{ mb: 4 }}
              options={jobsites || []}
              getOptionLabel={opt => opt.jobsiteName || ""}
              value={values.jobsite}
              onChange={handleJobsiteChange}
              disabled={!values.customer}            // only disabled until a customer is selected
              noOptionsText="No options"             // what to display when jobsites.length === 0
              renderInput={params => (
                <CustomTextField
                  {...params}
                  label="Jobsite Address *"
                  placeholder="Select Jobsite"
                  error={!!fieldErrors.jobsite}
                  helperText={fieldErrors.jobsite}
                />
              )}
            />
           <CustomAutocomplete
              fullWidth
              sx={{ mb: 4 }}
              options={
                svcList
                  ? [...svcList].sort((a, b) => {
                      const nameA = `${a.industryName ?? ""} - ${a.serviceOfferingName ?? ""}`.toLowerCase();
                      const nameB = `${b.industryName ?? ""} - ${b.serviceOfferingName ?? ""}`.toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                  : []
              }
              getOptionLabel={(opt) => `${opt.industryName} - ${opt.serviceOfferingName}`}
              value={values.service}
              onChange={handleServiceChange}
              noOptionsText="No services"
              disabled={isEdit && entityType === "Assessment"}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Service *"
                  placeholder="Select Service"
                  error={!!fieldErrors.service}
                  helperText={fieldErrors.service}
                />
              )}
            />
            <CustomTextField
              fullWidth
              sx={{ mb: 4 }}
              label={entityType === 'Assessment' ? 'Assessment Title' : 'Work Order Title'}
              placeholder={entityType === 'Assessment' ? 'Enter Assessment Title' : 'Enter Work Order Title'}
              value={values.requestTitle}
              onChange={handleRequestTitleChange}
              error={!!fieldErrors.requestTitle}
              helperText={fieldErrors.requestTitle}
            />
            <CustomTextField
              fullWidth
              multiline
              sx={{ mb: 4 }}
              label="Description"
              placeholder="Add a description..."
              value={values.description}
              onChange={handleDescriptionChange}
              error={!!fieldErrors.description}
              helperText={fieldErrors.description}
              rows={3}
            />
            <Box sx={{ mb: 4 }}>
              <FormLabel sx={{ fontSize: "13px", color: "text.primary", mb: 1 }}>
                Special Instructions (max 10)
              </FormLabel>

              {values.instructions.map((instr, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                  <CustomTextField
                    fullWidth
                    placeholder={`Instruction #${i + 1}`}
                    value={instr.instructionText}
                    onChange={e => handleInstructionChange(i, e)}
                  />
                  <IconButton size="small" onClick={() => handleRemoveInstruction(i)}>
                    <Icon icon="tabler:x" />
                  </IconButton>
                </Box>
              ))}

              {values.instructions.length < 10 && (
                <IconButton onClick={handleAddInstruction}>
                  <Icon icon="tabler:plus" />
                </IconButton>
              )}
            </Box>
           <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <FormLabel
                required
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  mb: 1,
                  color: "text.primary",
                  display: 'block'
                }}
              >
                {isUpdateMode ? "Date" : "Start Date"}
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: !!fieldErrors.startDateTime,
                      helperText: fieldErrors.startDateTime,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={12}>
              <FormLabel
                required
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  mb: 1,
                  color: "text.primary",
                  display: 'block'
                }}
              >
                {isUpdateMode ? "Time" : "Start Time"}
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  value={startTime}
                  onChange={handleStartTimeChange}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* <Grid item xs={12} md={6.8}>
              <FormLabel
                required
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  mb: 1,
                  color: "text.primary",
                  display: 'block' // Ensures label appears above input
                }}
              >
                End Date & Time
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDateTimePicker
                  value={endDateTime}
                  onChange={(d) => setEndDateTime(d)}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: !!fieldErrors.endDateTime,
                      helperText: fieldErrors.endDateTime,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid> */}
            {!isUpdateMode && (
              <>
                <Grid item xs={12} md={12}>
                  <FormLabel
                    required
                    sx={{
                      fontWeight: 400,
                      fontSize: "13px",
                      mb: 1,
                      color: "text.primary",
                      display: 'block'
                    }}
                  >
                    End Date
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={endDate}
                      onChange={handleEndDateChange}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!fieldErrors.endDateTime,
                          helperText: fieldErrors.endDateTime,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={12}>
                  <FormLabel
                    required
                    sx={{
                      fontWeight: 400,
                      fontSize: "13px",
                      mb: 1,
                      color: "text.primary",
                      display: 'block'
                    }}
                  >
                    End Time
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={endTime}
                      onChange={handleEndTimeChange}
                      slotProps={{
                        textField: {
                          size: "small",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
          </Grid>
          <br />
          <CustomAutocomplete
              multiple
              limitTags={2}
              fullWidth
              sx={{ mb: 4 }}
              options={
                empList
                  ? [...empList].sort((a, b) => {
                      const nameA = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase();
                      const nameB = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                  : []
              }
              getOptionLabel={(opt) => `${opt.firstName} ${opt.lastName}`}
              value={values.assignees || []}
              onChange={handleAssigneesChange}
              noOptionsText="No employees"
              filterOptions={(options, params) => {
                const q = params.inputValue?.trim().toLowerCase() || "";
                return options.filter((opt) =>
                  `${opt.firstName} ${opt.lastName}`.toLowerCase().includes(q)
                );
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Assign To"
                  placeholder="Select employees"
                />
              )}
            />


            <Box mb={4}>
              <FormLabel sx={{ fontSize: "13px", color: "text.primary", mb: 1 }}>
                Attachments
              </FormLabel>

              {/* hidden file input */}
              <input
                accept="*"
                id="attachment-input"
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <Box mt={1} display="flex" alignItems="center" gap={1}>
                {!attachment ? (
                  <label htmlFor="attachment-input">
                    <IconButton
                      component="span"
                      size="large"
                      sx={{
                        border: "1px dashed",
                        borderColor: "grey.400",
                        width: 48,
                        height: 48,
                      }}
                    >
                      <Icon icon="tabler:plus" fontSize="1.5rem" />
                    </IconButton>
                  </label>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title={attachment.fileName}>
                      <IconButton size="small">
                        <Icon icon="tabler:paperclip" fontSize="1.5rem" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => setAttachment(null)}
                    >
                      <Icon icon="tabler:x" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <RenderSidebarFooter />
            </Box>
          </form>
        </DatePickerWrapper>
      </Box>
      
    </Drawer>
     <UnsavedChangesDialog
      open={isDialogOpen}
      handleClose={handleDialogClose}
      handleDiscard={handleDiscardChanges}
    />
    </>
  );
};

export default AddEventSidebar;
